import React, { useEffect } from "react";
import { Route, Redirect, useHistory } from "react-router-dom";
import { Spinner } from "reactstrap";
import StorageService from '../../services/StorageService';
import { CLINICAL_ADMIN, SUPER_ADMIN } from '../../constants/roles';
import { ChosenPatientContext } from '../../pages/Dashboard/tableContext';
import { USER, SESSION } from '../../constants/storageConstants';
import { FALLBACK_ROUTE } from "../../constants/routes";
import { SECTION_CHECK_FAILED_MESSAGE, SECTION_LOCKED_MESSAGE } from "../../constants/sections";
import { useSectionAccess } from "../../components/sectionAccess/SectionAccessContext";
import toastr from "../../components/toastr/index";

const storageService = new StorageService();

/** A gated section whose access has not been checked for this page yet: wait rather than bounce. */
function SectionLoading() {
    return (
        <div className="page-content d-flex justify-content-center" data-testid="section-access-loading">
            <Spinner color="primary" />
        </div>
    );
}

/** A gated section this admin may not see: back to the fallback page, with one notice. */
function LockedSectionRedirect({ message }) {
    const history = useHistory();

    useEffect(() => {
        toastr.error(message);
        history.replace(FALLBACK_ROUTE);
    }, [history, message]);

    return null;
}

/**
 * What a gated section shows instead of its page, or null to open the page.
 *  - A super admin (by the last answer, or the stored role before any answer) opens it at once:
 *    they see everything, and a demotion ends their sessions on the server.
 *  - Anyone else waits until the check asked on arriving at this page has come back, so the page
 *    (and its API calls) never runs on the previous page's answer. A query-string-only change
 *    (pagination, search) is not a new arrival, so an open page stays mounted through its re-check.
 *  - Then the last successful answer decides (a failed re-check keeps it). With no answer ever,
 *    the stored-role fallback shows nothing, and the notice says the check failed.
 */
function sectionGate(section, sectionAccess) {
    if (!section || sectionAccess.role === SUPER_ADMIN) {
        return null;
    }
    if (!sectionAccess.settled) {
        return <SectionLoading />;
    }
    if (sectionAccess.sections[section]) {
        return null;
    }

    return (
        <LockedSectionRedirect
            message={sectionAccess.loaded ? SECTION_LOCKED_MESSAGE : SECTION_CHECK_FAILED_MESSAGE}
        />
    );
}

/**
 * @param {string[]} [roles] only these stored roles may open the route (Log history)
 * @param {string} [section] a gated section (constants/sections): shown only when the section
 *     access in force says so. Hide only - the API behind it is unchanged (Statistics excepted).
 */
function PrivateRoute({ layout: Layout, component: Component, roles= [], section, ...rest }) {
    const sectionAccess = useSectionAccess();
    const isTokenActual = storageService.getValueByKey(SESSION);
    const role = storageService.getValueByKey(USER)?.role;
    // A long-standing quirk: a stored Doctor (role 1) with no session is let through. Deliberately
    // NOT widened to Admin (4) or anyone else - with no session they are sent to /login.
    const isUserClinicalAdmin = role === CLINICAL_ADMIN;

    if (!isTokenActual && !isUserClinicalAdmin) {
        storageService.deleteValueByKey(SESSION);
        storageService.deleteValueByKey(USER);
        return (
            <Redirect to="/login" />
        );
    }

    if (roles.length && !roles.includes(role)) {
        return <Redirect to={FALLBACK_ROUTE} />;
    }

    return <Route {...rest} render={(props) => {
        const instead = sectionGate(section, sectionAccess);
        if (instead) {
            return <Layout>{instead}</Layout>;
        }

        return (
            <Layout>
                <ChosenPatientContext.Provider >
                    <Component {...props} />
                </ChosenPatientContext.Provider>
            </Layout>
        );
    }} />;
}

export default PrivateRoute;
